import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { Settings, Shield, Server, Zap, Users, Wrench, ChevronDown, ChevronRight, Save, RotateCcw, Check, Cpu } from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface SystemSettings {
  nameplate_display: 'name' | 'asset_tag' | 'serial';
  show_weight: boolean;
  show_power: boolean;
  show_airflow: boolean;
  show_tenant: boolean;
  show_groups: boolean;
  show_organizations: boolean;
  show_warranty: boolean;
  show_contacts: boolean;
  default_projection: 'front' | 'rear' | 'split';
  show_zero_u: boolean;
  show_empty_slots: boolean;
  ai_provider: string;
  openai_api_key: string;
  openai_model: string;
  openai_base_url: string;
}

const DEFAULT_SETTINGS: SystemSettings = {
  nameplate_display: 'name',
  show_weight: true,
  show_power: true,
  show_airflow: false,
  show_tenant: false,
  show_groups: false,
  show_organizations: false,
  show_warranty: false,
  show_contacts: false,
  default_projection: 'split',
  show_zero_u: true,
  show_empty_slots: true,
  ai_provider: 'ollama',
  openai_api_key: '',
  openai_model: 'gpt-4o-mini',
  openai_base_url: 'https://api.openai.com/v1',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Toggle({ value, onChange, disabled }: { value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors focus:outline-none
        ${value ? 'bg-blue-600 border-blue-500' : 'bg-slate-700 border-slate-600'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform
          ${value ? 'translate-x-4' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  accent?: string;
}

function Section({ icon, title, subtitle, children, accent = 'border-blue-600/40' }: SectionProps) {
  const [open, setOpen] = useState(true);
  return (
    <div className={`bg-slate-900 border border-slate-800 rounded-lg overflow-hidden`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/50 transition text-left"
      >
        <span className={`w-1 h-6 rounded-full bg-gradient-to-b ${accent}`} />
        <span className="text-slate-300">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-100">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-800/60">
          {children}
        </div>
      )}
    </div>
  );
}

function ToggleRow({ label, hint, value, onChange }: { label: string; hint?: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div>
        <div className="text-sm text-slate-200">{label}</div>
        {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <Toggle value={value} onChange={onChange} />
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function AdminSettingsPage() {
  const queryClient = useQueryClient();

  const { data: serverSettings, isLoading, error } = useQuery<SystemSettings>({
    queryKey: ['system-settings'],
    queryFn: () => api.get('/api/admin/system-settings').then(r => r.data),
    staleTime: 60_000,
  });

  const [draft, setDraft] = useState<SystemSettings>(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (serverSettings) setDraft(serverSettings);
  }, [serverSettings]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(serverSettings ?? DEFAULT_SETTINGS);

  const mutation = useMutation({
    mutationFn: (data: Partial<SystemSettings>) =>
      api.put('/api/admin/system-settings', data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['system-settings'], data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const set = <K extends keyof SystemSettings>(key: K, value: SystemSettings[K]) => {
    setDraft(d => ({ ...d, [key]: value }));
  };

  const handleSave = () => mutation.mutate(draft);

  const handleReset = () => {
    if (serverSettings) setDraft(serverSettings);
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500 gap-2">
        <div className="w-4 h-4 border-2 border-blue-500/40 border-t-blue-500 rounded-full animate-spin" />
        Загрузка настроек…
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-6 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm">
        ⚠ Не удалось загрузить настройки. Убедитесь, что у вас есть права администратора.
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 animate-fade-in space-y-6 font-sans">

      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings className="w-6 h-6 text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-slate-100">Настройки системы</h1>
          <p className="text-sm text-slate-500 mt-0.5">Конфигурация отображения — применяется ко всем пользователям</p>
        </div>
      </div>

      {/* Admin notice */}
      <div className="flex items-start gap-2.5 p-3 bg-amber-900/20 border border-amber-700/40 rounded-lg text-sm text-amber-300">
        <Shield className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Изменения применяются ко <strong>всем пользователям</strong> системы незамедлительно после сохранения.</span>
      </div>

      {/* ── Паспорт устройства ─────────────────────────────────────────── */}
      <Section
        icon={<Server className="w-4 h-4" />}
        title="Паспорт устройства"
        subtitle="Что отображать на блоке в стойке"
        accent="from-blue-500 to-blue-700"
      >
        <div className="pt-3 space-y-1.5">
          <p className="text-xs text-slate-500 mb-2">Основная надпись на блоке оборудования</p>
          {(['name', 'asset_tag', 'serial'] as const).map(opt => (
            <label key={opt} className="flex items-center gap-3 cursor-pointer py-1.5 px-2 rounded hover:bg-slate-800/50 transition">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition
                ${draft.nameplate_display === opt ? 'border-blue-500 bg-blue-600' : 'border-slate-600 bg-transparent'}`}>
                {draft.nameplate_display === opt && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
              </div>
              <div>
                <div className="text-sm text-slate-200">
                  {opt === 'name' ? 'Имя устройства' : opt === 'asset_tag' ? 'Asset Tag' : 'Серийный номер'}
                </div>
                <div className="text-xs text-slate-500">
                  {opt === 'name' ? 'Например: dc-alpha-tor-sw-1' : opt === 'asset_tag' ? 'Инвентарный номер' : 'Заводской S/N'}
                </div>
              </div>
              <input
                type="radio"
                className="sr-only"
                checked={draft.nameplate_display === opt}
                onChange={() => set('nameplate_display', opt)}
              />
            </label>
          ))}
        </div>
      </Section>

      {/* ── Физические параметры ────────────────────────────────────────── */}
      <Section
        icon={<Zap className="w-4 h-4" />}
        title="Физические параметры"
        subtitle="Показывать в паспорте и таблицах"
        accent="from-amber-500 to-amber-700"
      >
        <ToggleRow label="Масса (кг)" hint="Вес устройства, суммарный вес стойки" value={draft.show_weight} onChange={v => set('show_weight', v)} />
        <ToggleRow label="Мощность (Вт)" hint="Потребление, тепловыделение, нагрузка PDU" value={draft.show_power} onChange={v => set('show_power', v)} />
        <ToggleRow label="Воздушный поток" hint="Направление охлаждения (front-to-back / back-to-front)" value={draft.show_airflow} onChange={v => set('show_airflow', v)} />
      </Section>

      {/* ── Организационные данные ──────────────────────────────────────── */}
      <Section
        icon={<Users className="w-4 h-4" />}
        title="Организационные данные"
        subtitle="Опциональные поля для корпоративного использования"
        accent="from-purple-500 to-purple-700"
      >
        <ToggleRow label="Тенант (клиент / подразделение)" hint="Кому принадлежит устройство" value={draft.show_tenant} onChange={v => set('show_tenant', v)} />
        <ToggleRow label="Группы устройств" hint="Device Groups для логической группировки" value={draft.show_groups} onChange={v => set('show_groups', v)} />
        <ToggleRow label="Организация" hint="Юридическая организация-владелец" value={draft.show_organizations} onChange={v => set('show_organizations', v)} />
      </Section>

      {/* ── Техническое обслуживание ────────────────────────────────────── */}
      <Section
        icon={<Wrench className="w-4 h-4" />}
        title="Техническое обслуживание"
        subtitle="Данные сервисного контракта и поддержки"
        accent="from-emerald-500 to-emerald-700"
      >
        <ToggleRow label="Гарантия / сервисный контракт" hint="Дата окончания, статус поддержки" value={draft.show_warranty} onChange={v => set('show_warranty', v)} />
        <ToggleRow label="Контактное лицо / NOC" hint="Ответственный за оборудование" value={draft.show_contacts} onChange={v => set('show_contacts', v)} />
      </Section>

      {/* ── Отображение стойки ──────────────────────────────────────────── */}
      <Section
        icon={<Server className="w-4 h-4" />}
        title="Отображение стойки"
        subtitle="Умолчания для страницы стойки"
        accent="from-cyan-500 to-cyan-700"
      >
        <div className="pt-3 space-y-1.5">
          <p className="text-xs text-slate-500 mb-2">Режим просмотра по умолчанию</p>
          {(['split', 'front', 'rear'] as const).map(opt => (
            <label key={opt} className="flex items-center gap-3 cursor-pointer py-1.5 px-2 rounded hover:bg-slate-800/50 transition">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition
                ${draft.default_projection === opt ? 'border-cyan-500 bg-cyan-600' : 'border-slate-600 bg-transparent'}`}>
                {draft.default_projection === opt && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
              </div>
              <div className="text-sm text-slate-200">
                {opt === 'split' ? 'Фронт + Тыл (оба вида)' : opt === 'front' ? 'Только фронтальный вид' : 'Только вид сзади'}
              </div>
              <input type="radio" className="sr-only" checked={draft.default_projection === opt} onChange={() => set('default_projection', opt)} />
            </label>
          ))}
        </div>
        <div className="border-t border-slate-800/60 pt-3 mt-1">
          <ToggleRow label="Показывать 0U оборудование" hint="Устройства вертикального монтажа (кабельные лотки, PDU)" value={draft.show_zero_u} onChange={v => set('show_zero_u', v)} />
          <ToggleRow label="Показывать свободные слоты" hint="Блок ёмкостного планирования с перечнем свободных U в стойке" value={draft.show_empty_slots} onChange={v => set('show_empty_slots', v)} />
        </div>
      </Section>

      {/* ── Настройки ИИ-ассистента ────────────────────────────────────── */}
      <Section
        icon={<Cpu className="w-4 h-4" />}
        title="Настройки ИИ-ассистента"
        subtitle="Настройка провайдера и параметров языковой модели"
        accent="from-pink-500 to-pink-700"
      >
        <div className="space-y-4 pt-3 font-sans">
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">Провайдер ИИ</label>
            <select
              value={draft.ai_provider}
              onChange={e => set('ai_provider', e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500 transition"
            >
              <option value="ollama">Ollama (локальный инференс на VDS)</option>
              <option value="openai">Cloud API (OpenAI / Gemini / DeepSeek)</option>
            </select>
          </div>
          
          {draft.ai_provider === 'openai' && (
            <div className="space-y-3 border-t border-slate-800/60 pt-3">
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">API Key</label>
                <input
                  type="password"
                  value={draft.openai_api_key}
                  onChange={e => set('openai_api_key', e.target.value)}
                  placeholder={draft.openai_api_key === '******' ? '••••••••' : 'Введите API ключ'}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">Модель</label>
                <input
                  type="text"
                  value={draft.openai_model}
                  onChange={e => set('openai_model', e.target.value)}
                  placeholder="gpt-4o-mini"
                  className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">Базовый URL (API Base)</label>
                <input
                  type="text"
                  value={draft.openai_base_url}
                  onChange={e => set('openai_base_url', e.target.value)}
                  placeholder="https://api.openai.com/v1"
                  className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500 transition"
                />
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* ── Save/Reset bar ──────────────────────────────────────────────── */}
      <div className={`sticky bottom-4 flex items-center gap-3 p-3 rounded-lg border transition-all
        ${isDirty
          ? 'bg-slate-900 border-blue-600/60 shadow-lg shadow-blue-900/30'
          : 'bg-slate-900/40 border-slate-800'}`}>

        <div className="flex-1 text-xs text-slate-500">
          {saved
            ? <span className="text-emerald-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Сохранено</span>
            : isDirty
            ? 'Есть несохранённые изменения'
            : 'Все изменения сохранены'}
        </div>

        <button
          type="button"
          disabled={!isDirty || mutation.isPending}
          onClick={handleReset}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Сбросить
        </button>

        <button
          type="button"
          disabled={!isDirty || mutation.isPending}
          onClick={handleSave}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          {mutation.isPending ? (
            <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
          Сохранить настройки
        </button>
      </div>

    </div>
  );
}
